import React, { memo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  List,
  TextField,
  Filter,
  Datagrid,
  FunctionField,
  TextInput,
  DeleteButton,
  useNotify,
  usePermissions,
  useRefresh,
} from 'react-admin';
import { withStyles } from '@mui/styles';
import Button from '@mui/material/Button';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import IconButton from '@mui/material/IconButton';
import roles from '../../constants/roles';
import uploadVideoService from '../../utils/uploadVideo';

const VideoProcessingStatus = {
  1: 'Processing',
  2: 'Complete',
  3: 'Error',
};

const styles = {
  center: {
    textAlign: 'center',
  },
  uploadButton: {
    marginBottom: '1rem',
  },
  actionButton: {
    padding: 0,
  },
};

const ListVideosFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Search" source="search" alwaysOn />
  </Filter>
);

const ListVideos = ({ classes, ...props }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const navigate = useNavigate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { permissions } = usePermissions();

  const handleFileUpload = useCallback(
    (event) => {
      const file = event.target.files[0];
      if (file) {
        setUploading(true);
        setProgress(0);
        uploadVideoService({ rawFile: file }, (progressEvent) => {
          const currentProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          setProgress(currentProgress);
        })
          .then((result) => {
            if (result && result.id) {
              notify('Video uploaded successfully', { type: 'success' });
              refresh();
            } else {
              notify('Video upload failed', { type: 'error' });
            }
          })
          .catch(() => {
            notify('Video upload failed', { type: 'error' });
          })
          .finally(() => {
            setUploading(false);
            setProgress(0);
          });
      }
    },
    [notify, refresh],
  );

  const handleEdit = useCallback(
    (event, record) => {
      event.stopPropagation();
      navigate(`/file/video/${record.id}`);
    },
    [navigate],
  );

  return (
    <>
      <input
        accept="video/*"
        style={{ display: 'none' }}
        id="upload-video-button"
        type="file"
        onChange={handleFileUpload}
      />
      <label htmlFor="upload-video-button">
        <Button
          variant="contained"
          color="primary"
          component="span"
          className={classes.uploadButton}
          disabled={uploading}
        >
          {uploading ? `${progress}% Uploaded` : 'Upload Video'}
        </Button>
      </label>
      <List {...props} exporter={false} bulkActions={false} filters={<ListVideosFilter />}>
        <Datagrid rowClick="show">
          <TextField label="File Name" source="originalName" sortable={false} />
          <TextField label="Mux Id" source="muxPlaybackId" sortable={false} />
          <TextField label="Created At" source="createdAt" sortable />
          <FunctionField
            label="Processing Status"
            source="transcodingStatus"
            render={(record) => VideoProcessingStatus[record.transcodingStatus]}
            sortable={false}
          />
          <FunctionField
            label="Exercises"
            sortable={false}
            render={(record) => (
              <div>
                [
                {record?.exercises?.map((exercise, index) => (
                  <span key={exercise.id}>
                    {exercise.id}
                    {index < record.exercises.length - 1 ? ', ' : ''}
                  </span>
                ))}
                ]
              </div>
            )}
          />
          <FunctionField
            label="Stream"
            sortable={false}
            render={(record) => (
              <IconButton
                href={`https://stream.mux.com/${record.muxPlaybackId}/low.mp4`}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.actionButton}
                size="small"
              >
                <PlayArrowIcon />
              </IconButton>
            )}
          />
          <FunctionField
            label="Size"
            sortable
            render={(record) => `${(record.size / 1024 / 1024).toFixed(2)} MB`}
          />
          {permissions === roles.superAdmin && (
            <FunctionField
              label="Actions"
              render={(record) => (
                <>
                  <IconButton
                    onClick={(e) => handleEdit(e, record)}
                    className={classes.actionButton}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  {record?.exercises?.length === 0 && (
                    <DeleteButton resource="file/video" record={record} />
                  )}
                </>
              )}
            />
          )}
        </Datagrid>
      </List>
    </>
  );
};

export default memo(withStyles(styles)(ListVideos));
